/**
 * Company Registration component
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Loader2, 
  Building, 
  Lock, 
  Mail, 
  User, 
  Globe, 
  MapPin, 
  Users,
  FileText,
  GraduationCap
} from 'lucide-react';
import { registerCompany, isCompanyNameAvailable } from '../../../features/company/services/companyAuthService';

/**
 * Company Registration component
 * 
 * @param {Object} props - Component props
 * @param {Function} props.switchToLogin - Function to switch to login view
 * @returns {JSX.Element} Company registration component
 */
const CompanyRegister = ({ switchToLogin }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingName, setCheckingName] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    password: '',
    confirmPassword: '',
    contactPerson: '',
    industry: '',
    companySize: '',
    website: '',
    location: '',
    description: ''
  });
  const [errors, setErrors] = useState({});

  // Industry options
  const industries = [
    'Technology',
    'Finance',
    'Healthcare',
    'Education',
    'Retail',
    'Manufacturing',
    'Consulting',
    'Marketing',
    'Real Estate',
    'Non-profit',
    'Government',
    'Other'
  ];

  // Company size options
  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees'
  ];

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Check company name availability
  const checkCompanyName = async (companyName) => {
    if (!companyName || companyName.length < 2) return;
    
    setCheckingName(true);
    try {
      const isAvailable = await isCompanyNameAvailable(companyName);
      if (!isAvailable) {
        setErrors(prev => ({ 
          ...prev, 
          companyName: 'Company name is already taken' 
        }));
      }
    } catch (error) {
      console.error('Error checking company name:', error);
    } finally {
      setCheckingName(false);
    }
  };

  // Handle company name blur
  const handleCompanyNameBlur = () => {
    if (formData.companyName) {
      checkCompanyName(formData.companyName);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Company name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (formData.companyName.length < 2) {
      newErrors.companyName = 'Company name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Contact person validation
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }

    // Industry validation
    if (!formData.industry) {
      newErrors.industry = 'Please select an industry';
    }

    // Company size validation
    if (!formData.companySize) {
      newErrors.companySize = 'Please select company size';
    }

    // Location validation
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Company description is required';
    } else if (formData.description.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      const result = await registerCompany(formData);
      
      // Store token in local storage
      const token = await result.user.getIdToken();
      localStorage.setItem('companyAuthToken', token);
      
      // Navigate to company dashboard
      navigate('/company/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different error codes
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ email: 'An account with this email already exists' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: 'Invalid email address' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ password: 'Password is too weak' });
      } else {
        setErrors({ general: 'Registration failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                <span className="text-white font-bold text-2xl transform -rotate-12">V</span>
              </div>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">
            <span className="text-green-500">Varsity</span>Link
          </h1>
          <div className="flex items-center justify-center space-x-2 mt-2">
            <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full flex items-center">
              <Building className="w-3 h-3 mr-1" />
              Company Registration
            </span>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-8 space-y-6">
          <h2 className="text-2xl font-semibold text-gray-800 text-center">
            Join VarsityLink as an Employer
          </h2>
          
          <p className="text-gray-600 text-center">
            Connect with top university talent across South Africa
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Company Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Company Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="companyName"
                  placeholder="Your Company Name"
                  value={formData.companyName}
                  onChange={handleChange}
                  onBlur={handleCompanyNameBlur}
                  className={`pl-10 pr-10 py-3 w-full bg-gray-50 border ${
                    errors.companyName ? 'border-red-500' : 'border-gray-200'
                  } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {checkingName && (
                    <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                  )}
                </div>
              </div>
              {errors.companyName && (
                <p className="text-red-500 text-sm">{errors.companyName}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Company Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="company@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                    errors.email ? 'border-red-500' : 'border-gray-200'
                  } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email}</p>
              )}
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.password ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm">{errors.password}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* Contact Person */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Contact Person</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="contactPerson"
                  placeholder="Full Name"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                    errors.contactPerson ? 'border-red-500' : 'border-gray-200'
                  } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                />
              </div>
              {errors.contactPerson && (
                <p className="text-red-500 text-sm">{errors.contactPerson}</p>
              )}
            </div>

            {/* Industry and Company Size */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`pl-4 pr-4 py-3 w-full bg-gray-50 border ${
                    errors.industry ? 'border-red-500' : 'border-gray-200'
                  } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                >
                  <option value="">Select Industry</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
                {errors.industry && (
                  <p className="text-red-500 text-sm">{errors.industry}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Company Size</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Users className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.companySize ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  >
                    <option value="">Select Size</option>
                    {companySizes.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.companySize && (
                  <p className="text-red-500 text-sm">{errors.companySize}</p>
                )}
              </div>
            </div>

            {/* Website and Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Website (Optional)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="url"
                    name="website"
                    placeholder="https://www.company.com"
                    value={formData.website}
                    onChange={handleChange}
                    className="pl-10 pr-4 py-3 w-full bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Location</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="location"
                    placeholder="Cape Town, South Africa"
                    value={formData.location}
                    onChange={handleChange}
                    className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                      errors.location ? 'border-red-500' : 'border-gray-200'
                    } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                  />
                </div>
                {errors.location && (
                  <p className="text-red-500 text-sm">{errors.location}</p>
                )}
              </div>
            </div>

            {/* Company Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Company Description</label>
              <div className="relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  name="description"
                  placeholder="Tell us about your company, culture, and what makes you unique..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className={`pl-10 pr-4 py-3 w-full bg-gray-50 border ${
                    errors.description ? 'border-red-500' : 'border-gray-200'
                  } rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none`}
                />
              </div>
              <div className="flex justify-between items-center">
                {errors.description && (
                  <p className="text-red-500 text-sm">{errors.description}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.description.length}/500 characters (minimum 50)
                </p>
              </div>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 text-red-500 p-3 rounded-xl text-sm">
                {errors.general}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || checkingName}
              className={`w-full font-medium py-3 rounded-xl flex items-center justify-center space-x-2 transition-colors ${
                loading || checkingName
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Creating Account...</span>
                </>
              ) : (
                <span>Create Company Account</span>
              )}
            </button>

            {/* Terms Notice */}
            <p className="text-xs text-gray-500 text-center">
              By creating an account, you agree to our Terms of Service and Privacy Policy. 
              Company accounts are subject to verification before job posting privileges are activated.
            </p>
          </form>

          {/* Switch to Login */}
          <div className="text-center text-sm border-t border-gray-100 pt-6">
            <span className="text-gray-600">Already have a company account?</span>{' '}
            <button
              onClick={switchToLogin}
              className="text-green-500 hover:text-green-600 font-medium"
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegister;